import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { ICustomer } from '../../interfaces/Customer';
import type {
  DiscountReportData,
  FinancialSummaryParams,
  FinancialSummaryResponse,
  OrderHistoryReportParams,
  OrderHistoryReportResponse,
} from 'washday-sdk';
import { getFinancialSummary, getAnnualSalesStatistics, getAttendanceReport, getCashUpReportsByDateRange, getCashierBoxMovementsReport, getCleanedOrdersGraphDataReport, getCleanedOrdersReport, getCustomersReport, getDiscountCodesReport, getMonthSalesStatistics, getOrdersHistoryReport, getPaymentLinesReport, getPaymentMethodStatistics, getPopularDaysStatistics, getProductSalesReport, getProductSalesDrillDown, getProductsStatistics, getSalesReport, getScheduledDeliveries, getServicesToDeliverReport, getStaffActivityDetails, getStaffReport, getSuppliesReport, getUnpaidOrdersReport, getSupplyCostReport } from '../../services/reports.service';
const listQueryKey = 'reports';

export const useGetFinancialSummary = (params: FinancialSummaryParams) =>
  useQuery<FinancialSummaryResponse>({
    queryKey: [`${listQueryKey}/financial-summary`, params],
    enabled: Boolean(params.storeId) && params.storeId !== 'all',
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return (!status || status >= 500) && failureCount < 2;
    },
    queryFn: async () => {
      const response = await getFinancialSummary(params);
      if (!response?.data?.data) {
        throw new Error('Financial summary response does not match the expected contract.');
      }
      return response.data.data;
    }
  });

interface IQueryFnParams {
  orders: ICustomer[];
  totalRows: number;
}

export const useGetOrdersCleanedReportData = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
  pageNum?: number,
  limit: number | undefined,
}) => useInfiniteQuery<IQueryFnParams>({
  staleTime: 0,
  enabled: !!storeId,
  queryKey: [
    `${listQueryKey}/orders-cleaned-report`,
    storeId,
    query.pageNum,
    query.fromDate,
    query.toDate,
    query.limit,
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.orders.length, 0) || 0;
    const lastPageValue = (lastPage.totalRows) || 0;
    if (lastPageValue <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: { queryKey: any, pageParam: number }) => {
    try {
      const [_key] = queryKey;
      const res = await getCleanedOrdersReport(storeId, {
        ...query,
        pageNum: query.pageNum?.toString() || pageParam.toString(),
        limit: query.limit?.toString()
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryFnParams;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetCashUpReportsByDateRange = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  cashierBoxId?: string
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/cashups-report`,
    query.fromDate,
    query.toDate,
    query.cashierBoxId,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getCashUpReportsByDateRange(storeId, query);
      if (!res?.data?.data) {
        return { cashUpReports: [], totalRows: 0 };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetOrdersCleanedReportGraphData = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/orders-cleaned-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getCleanedOrdersGraphDataReport(storeId, query);
      if (!res?.data?.data?.graphData) {
        return [];
      }
      return res.data.data.graphData;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetSalesdReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
  type: "daily" | "month" | "year";
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/sales-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getSalesReport(storeId, query);
      if (!res?.data?.data?.tableInfo) {
        return {
          graphData: {}, tableInfo: [], totalRowStats: {
            totalPieces: 0,
            totalSales: 0,
            totalPaid: 0,
            totalUnpaid: 0,
            totalOrders: 0
          }
        };
      }
      return {
        graphData: res.data.data.graphData,
        tableInfo: res.data.data.tableInfo,
        totalRows: res.data.data.totalRows,
        totalRowStats: res.data.data.totalRowStats
      }
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetCustomersReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  sortBy?: 'lastOrderDate' | 'ordersQty' | 'ordersSalesTotal'
  sortOrder?: 'asc' | 'desc'
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/customers-report`,
    query.fromDate,
    query.toDate,
    query.sortBy,
    query.sortOrder,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getCustomersReport(storeId, query);
      if (!res?.data?.data?.tableInfo) {
        return { graphData: {}, tableInfo: [] };
      }
      return {
        graphData: res.data.data.graphData,
        tableInfo: res.data.data.tableInfo,
        totalRows: res.data.data.totalRows
      }
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetSuppliesReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
}) => useQuery<any>({
  enabled: true,
  queryKey: [
    storeId,
    `${listQueryKey}/supplies-cost-report`,
    query.fromDate,
    query.toDate,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const res = await getSupplyCostReport({
        store: storeId || 'all',
        fromDate: query.fromDate,
        toDate: query.toDate
      });
      if (!res?.data?.data?.items) {
        return { items: [], totalCost: 0, totalAmount: 0 };
      }

      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetStaffReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/staff-report`,
    query.fromDate,
    query.toDate,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getStaffReport(storeId, query);
      if (!res?.data?.data?.staff) {
        return { graphData: {}, tableInfo: [] };
      }

      return {
        graphData: res.data.data.graphData,
        tableInfo: res.data.data.staff,
      }
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetStaffActivityDetails = (
  storeId: string,
  employeeId: string | null,
  query: {
    fromDate?: string
    toDate?: string
  }
) => useQuery<any>({
  enabled: !!storeId && !!employeeId,
  queryKey: [
    storeId,
    `${listQueryKey}/staff-activity-details`,
    employeeId,
    query.fromDate,
    query.toDate,
  ],
  queryFn: async () => {
    try {
      const res = await getStaffActivityDetails(storeId, {
        employeeId: employeeId!,
        ...query,
      });
      if (!res?.data?.data?.details) {
        return { details: [] };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetDiscountCodeReport = (
  storeId: string,
  query: import('washday-sdk').DiscountCodeReportDateRangeParams,
) => useQuery<{
  graphData: DiscountReportData['graphData'];
  tableInfo: DiscountReportData['discountCodes'];
}>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/discountsCode-report`,
    query.fromDate,
    query.toDate,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getDiscountCodesReport(storeId, query);
      if (!res?.data?.data?.discountCodes) {
        return { graphData: [], tableInfo: [] };
      }

      return {
        graphData: res.data.data.graphData,
        tableInfo: res.data.data.discountCodes,
      }
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

interface IQueryUnpaidOrders {
  tableInfo: any[],
  totalRows: {
    totalAmount: number;
    totalAmountPaid: number;
    totalRowsCount: number;
    totalUnpaidAmount: number;
  }
}

export const useGetUnpaidOrdersReportData = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
  customerName?: string
  pageNum?: number,
  limit: number | undefined,
}) => useInfiniteQuery<IQueryUnpaidOrders>({
  staleTime: 0,
  enabled: !!storeId,
  queryKey: [
    `${listQueryKey}/unpaidOrders-report`,
    storeId,
    query.pageNum,
    query.fromDate,
    query.customerName,
    query.toDate,
    query.limit,
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.tableInfo.length, 0) || 0;
    const lastPageValue = (lastPage.totalRows) || 0;
    if (lastPageValue.totalRowsCount <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: { queryKey: any, pageParam: number }) => {
    try {
      const [_key] = queryKey;
      const res = await getUnpaidOrdersReport(storeId, {
        ...query,
        pageNum: query.pageNum?.toString() || pageParam.toString(),
        limit: query.limit?.toString()
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryUnpaidOrders;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

interface IQueryPaymentLines {
  graphInfo: any[],
  totalRows: number;
}

export const useGetPaymentLinesReportData = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
  pageNum?: number,
  limit: number | undefined,
}) => useInfiniteQuery<IQueryPaymentLines>({
  staleTime: 0,
  enabled: !!storeId,
  queryKey: [
    `${listQueryKey}/paymentLines-report`,
    storeId,
    query.pageNum,
    query.fromDate,
    query.timezone,
    query.toDate,
    query.limit,
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.graphInfo.length, 0) || 0;
    const lastPageValue = (lastPage.totalRows) || 0;
    if (lastPageValue <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: { queryKey: any, pageParam: number }) => {
    try {
      const [_key] = queryKey;
      const res = await getPaymentLinesReport(storeId, {
        ...query,
        pageNum: query.pageNum?.toString() || pageParam.toString(),
        limit: query.limit?.toString()
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryPaymentLines;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

interface IQueryCashierBoxMovements {
  graphInfo: any[],
  totalRows: number;
}

export const useGetCashierBoxMovementsReportData = (storeId: string, query: {
  cashierBoxId?: string
  fromDate?: string
  toDate?: string
  timezone?: string
  pageNum?: number,
  limit: number | undefined,
}) => useInfiniteQuery<IQueryCashierBoxMovements>({
  staleTime: 0,
  enabled: !!storeId,
  queryKey: [
    `${listQueryKey}/cashierBoxMovements-report`,
    storeId,
    query.cashierBoxId,
    query.pageNum,
    query.fromDate,
    query.timezone,
    query.toDate,
    query.limit,
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.graphInfo.length, 0) || 0;
    const lastPageValue = (lastPage.totalRows) || 0;
    if (lastPageValue <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: { queryKey: any, pageParam: number }) => {
    try {
      const [_key] = queryKey;
      const res = await getCashierBoxMovementsReport(storeId, {
        ...query,
        pageNum: query.pageNum?.toString() || pageParam.toString(),
        limit: query.limit?.toString()
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryCashierBoxMovements;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetProductSalesReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
  groupBySection?: boolean
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/productSales-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
    query.groupBySection,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getProductSalesReport(storeId, query);
      if (!res?.data?.data?.productsInfo) {
        return { graphData: {}, tableInfo: [] };
      }

      return {
        tableInfo: res.data.data.productsInfo,
        sectionsInfo: res.data.data.sectionsInfo,
        ordersInfo: res.data.data.ordersInfo
      }
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetProductSalesDrillDown = (
  storeId: string,
  storeProductId: string | null,
  query: {
    fromDate?: string
    toDate?: string
    timezone?: string
  }
) => useQuery<any>({
  enabled: !!storeId && !!storeProductId,
  queryKey: [
    storeId,
    `${listQueryKey}/productSales-drill-down`,
    storeProductId,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async () => {
    try {
      const res = await getProductSalesDrillDown(storeId, {
        storeProductId: storeProductId!,
        ...query
      });
      if (!res?.data?.data) {
        return { rows: [], totalRows: 0, totalQuantity: 0, totalAmount: 0 };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetAnnualStatisticsReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
}) => useQuery<{
  annualReport: {
    collectedOrders: number;
    ordersCount: number;
    paid: number;
    sales: number;
    unpaid: number;
    orders: {
      [key: string]: number;
    }
  }
}>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/statistics-annual-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getAnnualSalesStatistics(storeId, query);
      if (!res?.data?.data) {
        return {};
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetPaymentMethodStatisticsReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
}) => useQuery<{
  ordersReport: {
    card: number;
    cash: number;
    delivery: number;
    transfer: number;
  }
}>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/statistics-paymentMethod-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getPaymentMethodStatistics(storeId, query);
      if (!res?.data?.data) {
        return {};
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetProductStatisticsReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
}) => useQuery<{
  productsReport: {
    name
    :
    "Inventory Test Lavanderia"
    ordersCountPerMonth: number;
    productId: string;
    quantity: number;
    _id: {
      name: string;
      productId: string;
    }
  }[]
}>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/statistics-product-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getProductsStatistics(storeId, query);
      if (!res?.data?.data) {
        return {};
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetMonthSalesStatisticsReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
}) => useQuery<{
  monthReport: {
    cancelledOrders: number;
    collectedOrders: number;
    ordersCount: number;
    paid: number;
    sales: number;
    unpaid: number;
    orders: {
      [key: string]: number;
    }
  }
}>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/statistics-monthSales-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getMonthSalesStatistics(storeId, query);
      if (!res?.data?.data) {
        return {};
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetPopularDaysStatisticsReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  timezone?: string
}) => useQuery<{
  popularDaysReport: {
    Friday: number;
    Monday: number;
    Saturday: number;
    Sunday: number;
    Thursday: number;
    Tuesday: number;
    Wednesday: number;
  }
}>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/statistics-popularDays-report`,
    query.fromDate,
    query.toDate,
    query.timezone,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getPopularDaysStatistics(storeId, query);
      if (!res?.data?.data) {
        return {};
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetAttendanceReport = (storeId: string, query: {
  fromDate?: string
  toDate?: string
  employeeId?: string | null
  pageNum?: number
  limit?: number
}) => useQuery<any>({
  enabled: !!storeId,
  queryKey: [
    storeId,
    `${listQueryKey}/attendance-report`,
    query.fromDate,
    query.toDate,
    query.employeeId,
    query.pageNum,
    query.limit,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getAttendanceReport(storeId, query);
      if (!res?.data?.data) {
        return { tableInfo: [], totalRows: 0, pagination: { currentPage: 1, totalPages: 1, totalRecords: 0 } };
      }
      return {
        tableInfo: res.data.data.tableInfo || [],
        totalRows: res.data.data.totalRows || 0,
        pagination: res.data.data.pagination || { currentPage: 1, totalPages: 1, totalRecords: 0 }
      };
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetServicesToDeliverReport = (companyId: string, query: {
  storeId?: string
  dateFrom?: string
  dateTo?: string
  hideReady?: string
}, options?: { enabled?: boolean }) => useQuery<any>({
  enabled: options?.enabled !== false && !!companyId,
  queryKey: [
    companyId,
    `${listQueryKey}/services-to-deliver`,
    query.storeId,
    query.dateFrom,
    query.dateTo,
    query.hideReady,
  ],
  queryFn: async () => {
    try {
      const res = await getServicesToDeliverReport(companyId, query);
      if (!res?.data?.data) {
        return {
          summary: { services: [], totals: { servicesCount: 0, linesCount: 0, ordersCount: 0 } },
          items: [],
          meta: {}
        };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

const emptyOrderHistoryReport: OrderHistoryReportResponse = {
  items: [],
  pagination: { page: 1, limit: 25, totalRows: 0, totalPages: 0 },
  summary: {
    totalOrders: 0,
    pickupOrders: 0,
    deliveryOrders: 0,
    pickupAndDeliveryOrders: 0,
  },
};

export const useGetOrdersHistoryReport = (
  companyId: string,
  query: OrderHistoryReportParams,
) => useQuery<OrderHistoryReportResponse>({
  enabled: !!companyId && !!query.storeId,
  queryKey: [
    companyId,
    `${listQueryKey}/orders-history`,
    query.storeId,
    query.dateField,
    query.dateFrom,
    query.dateTo,
    query.statuses,
    query.sequence,
    query.homeServiceOnly,
    query.page,
    query.limit,
    query.sortDirection,
  ],
  queryFn: async () => {
    try {
      const res = await getOrdersHistoryReport(companyId, query);
      return res?.data?.data || emptyOrderHistoryReport;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  },
});

export const useGetScheduledDeliveries = (companyId: string, query: {
  storeId?: string
  dateFrom?: string
  dateTo?: string
  includeDelivered?: string
}) => useQuery<any>({
  enabled: !!companyId,
  queryKey: [
    companyId,
    `${listQueryKey}/scheduled-deliveries`,
    query.storeId,
    query.dateFrom,
    query.dateTo,
    query.includeDelivered,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getScheduledDeliveries(companyId, query);
      if (!res?.data?.data) {
        return {
          items: <{
            storeId: any;
            storeName: string;
            orders: any;
          }[]>[], meta: <{
            companyId: string;
            storeId: string;
            dateFrom: string;
            dateTo: string;
            includeDelivered: string;
          }>{}
        };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});
